import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Heading,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberInput,
  NumberInputField,
  Select,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Table,
  Tabs,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  VStack,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import {
  AlertTriangle,
  Boxes,
  ChevronDown,
  Edit,
  Eye,
  History,
  PackageCheck,
  PackageMinus,
  PackagePlus,
  RefreshCcw,
  Search,
  ShieldCheck,
  Store,
  Warehouse,
} from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";

import { useAuth } from "../auth/AuthContext";
import { db } from "../configs/firebase";
import { useCustomTheme } from "../contexts/CustomThemeContext";
import { useGetProductsQuery } from "../productApi/productAPI";
import { Product } from "../types/product";
import {
  InventoryMovement,
  InventoryStockRow,
  InventoryStockStatus,
  useAdjustStockMutation,
  useCreateOrUpdateStockMutation,
  useExpireReservationsMutation,
  useGetInventorySummaryQuery,
  useGetProductInventoryQuery,
  useGetSellerInventoryQuery,
} from "./inventoryApi";
import { useUserDisplayNamesFromAuth } from "../utils/userDisplayName";

type ProductLike = Product & {
  _id?: string;
  id?: string;
  userId?: string;
  sellerUserId?: string;
  ownerId?: string;
  title?: string;
  category?: string;
  imageUrl?: string;
  thumbnail?: string;
  images?: string[];
  price?: number;
  stockQty?: number;
  status?: string;
  brand?: string;
  type?: string;
  productType?: string;
  listingType?: string;
  hasVariants?: boolean;
  variantAttributes?: Array<{
    name?: string;
    options?: Array<{
      label?: string;
      value?: string;
      sku?: string;
      stockQty?: number;
      price?: number;
      isActive?: boolean;
    }>;
  }>;
};

type StockFormState = {
  productId: string;
  sellerUserId: string;
  warehouseId: string;
  variantKey: string;
  sku: string;
  setOnHandQty: string;
  quantity: string;
  lowStockThreshold: string;
  reorderPoint: string;
  reason: string;
  note: string;
};

type VariantOptionChoice = {
  label: string;
  variantKey: string;
  sku: string;
  stockQty: number;
};

type SellerOption = {
  sellerId: string;
  sellerName: string;
  productCount: number;
  categories: string[];
};

const emptyForm: StockFormState = {
  productId: "",
  sellerUserId: "",
  warehouseId: "",
  variantKey: "default",
  sku: "",
  setOnHandQty: "",
  quantity: "",
  lowStockThreshold: "3",
  reorderPoint: "5",
  reason: "",
  note: "",
};

const PRODUCT_TABLE_PAGE_SIZE = 20;

const formatNumber = (value: any) => {
  if (value === null || value === undefined || value === "") return "0";

  if (typeof value === "string" && value.includes("/")) {
    return value;
  }

  const numericValue = Number(value);

  if (Number.isNaN(numericValue)) {
    return String(value);
  }

  return numericValue.toLocaleString();
};

const formatMoney = (value: any) =>
  `MWK ${Number(value || 0).toLocaleString()}`;

const clean = (value: any) => String(value || "").trim();

const normalize = (value: any) => clean(value).toLowerCase();

const normalizeVariantValue = (value: any) =>
  clean(value)
    .toLowerCase()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const getProductId = (product?: ProductLike | null) =>
  clean(product?._id || product?.id);

const getProductSellerUserId = (product?: ProductLike | null) =>
  clean(product?.sellerUserId || product?.ownerId || product?.userId);

const getProductTitle = (product?: ProductLike | null) =>
  clean(product?.title) || getProductId(product) || "Product";

const getProductCategory = (product?: ProductLike | null) =>
  clean(product?.category) || "Uncategorised";

const getProductImage = (product?: ProductLike | null) =>
  product?.thumbnail || product?.imageUrl || product?.images?.[0] || "";

const getDefaultSellerUserId = (user: any) =>
  clean(user?.uid || user?.id || user?._id || user?.userId);

const getUserRoles = (user: any): string[] => {
  const roleCandidates = [
    user?.role,
    user?.userRole,
    user?.accountRole,
    user?.adminRole,
    user?.claims?.role,
    user?.customClaims?.role,
  ];

  const arrayCandidates = [
    user?.roles,
    user?.claims?.roles,
    user?.customClaims?.roles,
    user?.permissions,
  ];

  const singleRoles = roleCandidates
    .map((role) => normalize(role))
    .filter(Boolean);

  const arrayRoles = arrayCandidates
    .flatMap((roles) => (Array.isArray(roles) ? roles : []))
    .map((role) => normalize(role))
    .filter(Boolean);

  return Array.from(new Set([...singleRoles, ...arrayRoles]));
};

const isSuperAdminUser = (user: any) => {
  const roles = getUserRoles(user);

  // Only the platform superadmin receives cross-account inventory access.
  // Admin, owner, manager, shop and staff remain scoped to their own UID.
  return roles.some((role) => role.replace(/[\s_-]+/g, "") === "superadmin");
};

const getInlineAccessRole = (user: any) =>
  user?.role ||
  user?.userRole ||
  user?.accountRole ||
  user?.adminRole ||
  user?.claims?.role ||
  user?.customClaims?.role ||
  "";

const getStatusColor = (status?: InventoryStockStatus | string) => {
  switch (status) {
    case "in_stock":
    case "active":
      return "green";
    case "low_stock":
      return "orange";
    case "out_of_stock":
      return "red";
    case "disabled":
    case "archived":
    case "draft":
      return "gray";
    default:
      return "gray";
  }
};

const getStatusLabel = (status?: InventoryStockStatus | string) => {
  switch (status) {
    case "in_stock":
      return "In stock";
    case "low_stock":
      return "Low stock";
    case "out_of_stock":
      return "Out of stock";
    case "disabled":
      return "Disabled";
    case "active":
      return "Active";
    case "draft":
      return "Draft";
    case "archived":
      return "Archived";
    default:
      return "Unknown";
  }
};

const getMovementLabel = (type?: string) => {
  switch (type) {
    case "stock_created":
      return "Created";
    case "stock_adjusted":
      return "Adjusted";
    case "stock_reserved":
      return "Reserved";
    case "reservation_released":
      return "Released";
    case "reservation_expired":
      return "Expired";
    case "sale_committed":
      return "Sold";
    case "return_received":
      return "Returned";
    case "stock_damaged":
      return "Damaged";
    case "stock_restocked":
      return "Restocked";
    case "stock_transfer_in":
      return "Transfer In";
    case "stock_transfer_out":
      return "Transfer Out";
    default:
      return type || "Movement";
  }
};

const getMovementColor = (type?: string) => {
  switch (type) {
    case "stock_created":
    case "stock_restocked":
    case "return_received":
    case "stock_transfer_in":
      return "green";
    case "stock_reserved":
      return "blue";
    case "sale_committed":
      return "purple";
    case "reservation_released":
    case "reservation_expired":
      return "orange";
    case "stock_damaged":
    case "stock_transfer_out":
      return "red";
    default:
      return "gray";
  }
};

const getErrorMessage = (err: any, fallback: string) => {
  if (err?.data?.message) return err.data.message;
  if (err?.message) return err.message;
  return fallback;
};

const isLikelyNonStockProduct = (product?: ProductLike | null) => {
  const category = normalize(product?.category);
  const title = normalize(product?.title);
  const type = normalize(product?.type);
  const productType = normalize(product?.productType);
  const listingType = normalize(product?.listingType);

  const text = `${category} ${title} ${type} ${productType} ${listingType}`;

  return [
    "food",
    "fast food",
    "restaurant",
    "meal",
    "pizza",
    "burger",
    "drink",
    "beverage",
    "service",
    "delivery",
    "accommodation",
    "hotel",
    "room",
    "lodge",
    "flight",
    "bus",
    "ticket",
    "trip",
    "hike",
    "car hire",
    "rental",
    "booking",
  ].some((word) => text.includes(word));
};

const isSellableProductForInventory = (product?: ProductLike | null) => {
  const sellerId = getProductSellerUserId(product);
  const status = normalize(product?.status);
  const category = normalize(product?.category);
  const title = normalize(product?.title);
  const type = normalize(product?.type);
  const productType = normalize(product?.productType);
  const listingType = normalize(product?.listingType);

  const text = `${category} ${title} ${type} ${productType} ${listingType}`;

  if (!sellerId) return false;

  if (["archived", "deleted", "inactive", "disabled"].includes(status)) {
    return false;
  }

  return ![
    "accommodation",
    "hotel",
    "room",
    "lodge",
    "guest house",
    "flight",
    "bus",
    "ticket",
    "trip",
    "hike",
    "car hire",
    "car rental",
    "vehicle hire",
    "rental",
    "booking",
    "delivery",
    "service",
  ].some((word) => text.includes(word));
};

const buildVariantKey = (attributeName: string, optionValue: string) => {
  if (!attributeName || !optionValue) return "default";

  return `${normalizeVariantValue(attributeName)}=${normalizeVariantValue(
    optionValue,
  )}`;
};

const getVariantChoicesFromProduct = (
  product?: ProductLike | null,
): VariantOptionChoice[] => {
  const attributes = Array.isArray(product?.variantAttributes)
    ? product?.variantAttributes || []
    : [];

  const choices = attributes.flatMap((attribute) => {
    const attributeName = clean(attribute?.name);
    const options = Array.isArray(attribute?.options) ? attribute.options : [];

    return options
      .map((option) => {
        const optionValue = clean(option?.value || option?.label);

        if (!attributeName || !optionValue) return null;

        return {
          label: `${attributeName}: ${option?.label || optionValue}`,
          variantKey: buildVariantKey(attributeName, optionValue),
          sku: clean(option?.sku),
          stockQty: Number(option?.stockQty || 0),
        };
      })
      .filter(Boolean) as VariantOptionChoice[];
  });

  return choices.length > 0
    ? choices
    : [
        {
          label: "Default",
          variantKey: "default",
          sku: "",
          stockQty: Number(product?.stockQty || 0),
        },
      ];
};

const getFirstStockForProduct = (
  product: ProductLike | null,
  stockRows: InventoryStockRow[] = [],
) => {
  if (!product) return null;

  const productId = getProductId(product);
  const sellerUserId = getProductSellerUserId(product);

  return (
    stockRows.find(
      (row) =>
        row.productId === productId &&
        (!sellerUserId || row.sellerUserId === sellerUserId),
    ) ||
    stockRows.find((row) => row.productId === productId) ||
    null
  );
};

const getInventoryBadge = ({
  product,
  inventoryRow,
}: {
  product: ProductLike;
  inventoryRow?: InventoryStockRow | null;
}) => {
  if (inventoryRow) {
    return {
      label: getStatusLabel(inventoryRow.status),
      color: getStatusColor(inventoryRow.status),
      description: `${formatNumber(inventoryRow.availableQty)} available`,
    };
  }

  if (isLikelyNonStockProduct(product)) {
    return {
      label: "Inventory optional",
      color: "purple",
      description: "May be made fresh, booked, or service-based",
    };
  }

  if (Number(product.stockQty || 0) > 0) {
    return {
      label: "Legacy stock only",
      color: "blue",
      description: `${formatNumber(product.stockQty)} on product`,
    };
  }

  return {
    label: "No inventory",
    color: "orange",
    description: "Create stock record to track balance",
  };
};

const InventoryAdminPage = () => {
  const toast = useToast();
  const { user } = useAuth();
  const { theme } = useCustomTheme();
  const { getDisplayName, loading: usersLoading } =
    useUserDisplayNamesFromAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialProductId = searchParams.get("productId") || "";
  const defaultSellerUserId = getDefaultSellerUserId(user);

  const [resolvedAccessRole, setResolvedAccessRole] = useState(() =>
    getInlineAccessRole(user),
  );
  const [accessLoading, setAccessLoading] = useState(true);

  const isSuperAdmin = isSuperAdminUser({
    ...user,
    role: resolvedAccessRole,
    userRole: resolvedAccessRole,
  });

  const [sellerUserId, setSellerUserId] = useState(defaultSellerUserId);
  const [sellerSearch, setSellerSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [inventoryFilter, setInventoryFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState<ProductLike | null>(
    null,
  );
  const [selectedProductId, setSelectedProductId] = useState(initialProductId);
  const [selectedInventoryRow, setSelectedInventoryRow] =
    useState<InventoryStockRow | null>(null);
  const [hydratedProductId, setHydratedProductId] = useState("");
  const [form, setForm] = useState<StockFormState>({
    ...emptyForm,
    sellerUserId: defaultSellerUserId,
  });

  const manageDisclosure = useDisclosure();
  const movementsDisclosure = useDisclosure();

  const isDark = theme.name === "dark";

  const cardBg = theme.surface;
  const softBg = theme.surfaceAlt;
  const pageBg = theme.background;
  const textColor = theme.text;
  const mutedText = theme.textMuted;
  const borderColor = theme.border;
  const borderStrong = theme.borderStrong;

  const primaryButtonStyles = {
    color: theme.textOnPrimary,
    bgImage: theme.gradients?.primaryButton || theme.primary,
    boxShadow: theme.shadowSm,
    _hover: {
      bgImage: theme.gradients?.primaryButtonHover || theme.primaryHover,
      transform: "translateY(-1px)",
      boxShadow: theme.shadowMd,
    },
    _active: { transform: "translateY(0)" },
    borderRadius: "12px",
    transition: "all 0.2s ease",
  };

  const secondaryButtonStyles = {
    color: textColor,
    bg: isDark ? "rgba(255,255,255,0.04)" : "white",
    border: `1px solid ${borderColor}`,
    _hover: {
      bg: isDark ? "rgba(255,255,255,0.07)" : "rgba(42,44,59,0.03)",
      borderColor: borderStrong,
    },
    borderRadius: "12px",
    transition: "all 0.2s ease",
  };

  const compactMenuListStyles = {
    bg: cardBg,
    border: `1px solid ${borderColor}`,
    borderRadius: "16px",
    boxShadow: theme.shadowMd,
    p: 1.5,
  };

  const getSellerName = (sellerId: string) => {
    if (!sellerId) return "Missing seller";
    if (usersLoading) return "Loading seller...";
    return getDisplayName(sellerId);
  };

  useEffect(() => {
    let cancelled = false;

    const loadAccess = async () => {
      if (!user?.uid) {
        if (!cancelled) {
          setResolvedAccessRole("");
          setAccessLoading(false);
        }
        return;
      }

      setAccessLoading(true);

      try {
        const snapshot = await getDoc(doc(db, "users", user.uid));
        const profile = snapshot.exists() ? snapshot.data() : {};
        const role =
          profile?.role || profile?.userRole || getInlineAccessRole(user);

        if (!cancelled) setResolvedAccessRole(String(role || ""));
      } catch (accessError) {
        console.error("Failed to resolve Inventory Admin access:", accessError);

        if (!cancelled) {
          setResolvedAccessRole(String(getInlineAccessRole(user) || ""));
        }
      } finally {
        if (!cancelled) setAccessLoading(false);
      }
    };

    void loadAccess();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const {
    data: products = [],
    isLoading: productsLoading,
    refetch: refetchProducts,
  } = useGetProductsQuery();

  const {
    data: summary,
    isLoading: summaryLoading,
    refetch: refetchSummary,
  } = useGetInventorySummaryQuery(sellerUserId || undefined);

  const {
    data: sellerInventory,
    isLoading: sellerInventoryLoading,
    isFetching: sellerInventoryFetching,
    refetch: refetchSellerInventory,
  } = useGetSellerInventoryQuery(
    {
      sellerUserId,
      page: 1,
      limit: 500,
    },
    {
      skip: !sellerUserId,
    },
  );

  const {
    data: productInventory,
    isLoading: productInventoryLoading,
    isFetching: productInventoryFetching,
    refetch: refetchProductInventory,
  } = useGetProductInventoryQuery(selectedProductId, {
    skip: !selectedProductId,
  });

  const [createOrUpdateStock, { isLoading: creatingStock }] =
    useCreateOrUpdateStockMutation();

  const [adjustStock, { isLoading: adjustingStock }] = useAdjustStockMutation();

  const [expireReservations, { isLoading: expiringReservations }] =
    useExpireReservationsMutation();

  const allProducts = useMemo(
    () => (Array.isArray(products) ? (products as ProductLike[]) : []),
    [products],
  );

  const sellableProducts = useMemo(() => {
    const sellable = allProducts.filter(isSellableProductForInventory);

    if (isSuperAdmin) return sellable;
    if (!defaultSellerUserId) return [];

    return sellable.filter(
      (product) => getProductSellerUserId(product) === defaultSellerUserId,
    );
  }, [allProducts, defaultSellerUserId, isSuperAdmin]);

  const inventoryRows = sellerInventory?.rows || [];
  const selectedProductStockRows = productInventory?.stock || [];
  const movements = productInventory?.movements || [];

  const inventoryByProductId = useMemo(() => {
    const map = new Map<string, InventoryStockRow[]>();

    inventoryRows.forEach((row) => {
      const existing = map.get(row.productId) || [];
      map.set(row.productId, [...existing, row]);
    });

    return map;
  }, [inventoryRows]);

  const sellerOptions = useMemo<SellerOption[]>(() => {
    const sellersMap = new Map<string, SellerOption>();

    sellableProducts.forEach((product) => {
      const sellerId = getProductSellerUserId(product);
      const category = getProductCategory(product);
      const existing = sellersMap.get(sellerId);

      if (existing) {
        existing.productCount += 1;

        if (!existing.categories.includes(category)) {
          existing.categories.push(category);
        }

        return;
      }

      sellersMap.set(sellerId, {
        sellerId,
        sellerName: getSellerName(sellerId),
        productCount: 1,
        categories: category ? [category] : [],
      });
    });

    return Array.from(sellersMap.values()).sort((a, b) =>
      a.sellerName.localeCompare(b.sellerName),
    );
  }, [sellableProducts, getDisplayName, usersLoading]);

  const selectedSellerOption = useMemo(
    () => sellerOptions.find((seller) => seller.sellerId === sellerUserId),
    [sellerOptions, sellerUserId],
  );

  const filteredSellerOptions = useMemo(() => {
    const term = normalize(sellerSearch);

    if (!term) return sellerOptions;

    return sellerOptions.filter((seller) =>
      [
        seller.sellerName,
        seller.sellerId,
        seller.categories.join(" "),
        String(seller.productCount),
      ].some((value) => normalize(value).includes(term)),
    );
  }, [sellerOptions, sellerSearch]);

  const categories = useMemo(() => {
    const values = new Set<string>();

    sellableProducts
      .filter((product) => getProductSellerUserId(product) === sellerUserId)
      .forEach((product) => {
        const category = getProductCategory(product);
        if (category) values.add(category);
      });

    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [sellableProducts, sellerUserId]);

  const categoryOptions = useMemo(
    () => [
      { label: "All categories", value: "all" },
      ...categories.map((category) => ({
        label: category,
        value: category,
      })),
    ],
    [categories],
  );

  const statusOptions = useMemo(
    () => [
      {
        label: "All statuses",
        value: "all",
        description: "Show all product statuses",
      },
      {
        label: "Active",
        value: "active",
        description: "Visible and sellable",
      },
      {
        label: "Draft",
        value: "draft",
        description: "Not ready yet",
      },
      {
        label: "Out of stock",
        value: "out_of_stock",
        description: "Marked out of stock",
      },
      {
        label: "Archived",
        value: "archived",
        description: "Hidden or old listings",
      },
    ],
    [],
  );

  const inventoryStateOptions = useMemo(
    () => [
      {
        label: "All inventory states",
        value: "all",
        description: "Show every stock state",
      },
      {
        label: "With inventory",
        value: "withInventory",
        description: "Has stock records",
      },
      {
        label: "No inventory",
        value: "withoutInventory",
        description: "Missing stock records",
      },
      {
        label: "Optional inventory",
        value: "optionalInventory",
        description: "Food or service-like items",
      },
      {
        label: "Low stock",
        value: "lowStock",
        description: "Needs restocking",
      },
      {
        label: "Inventory out",
        value: "outOfStock",
        description: "No available stock",
      },
    ],
    [],
  );

  const productRows = useMemo(() => {
    const term = normalize(search);

    return sellableProducts
      .filter((product) => {
        const productId = getProductId(product);
        const sellerId = getProductSellerUserId(product);
        const sellerName = normalize(getSellerName(sellerId));
        const title = normalize(product.title);
        const category = normalize(product.category);
        const brand = normalize(product.brand);
        const status = normalize(product.status);
        const inventoryForProduct = inventoryByProductId.get(productId) || [];
        const firstInventory = inventoryForProduct[0] || null;
        const hasInventory = inventoryForProduct.length > 0;

        if (!sellerUserId || sellerId !== sellerUserId) {
          return false;
        }

        if (
          term &&
          ![
            productId,
            sellerId,
            sellerName,
            title,
            category,
            brand,
            normalize(product.status),
          ].some((value) => value.includes(term))
        ) {
          return false;
        }

        if (
          categoryFilter !== "all" &&
          normalize(categoryFilter) !== category
        ) {
          return false;
        }

        if (statusFilter !== "all" && status !== normalize(statusFilter)) {
          return false;
        }

        if (inventoryFilter === "withInventory" && !hasInventory) {
          return false;
        }

        if (inventoryFilter === "withoutInventory" && hasInventory) {
          return false;
        }

        if (
          inventoryFilter === "optionalInventory" &&
          !isLikelyNonStockProduct(product)
        ) {
          return false;
        }

        if (
          inventoryFilter === "lowStock" &&
          firstInventory?.status !== "low_stock"
        ) {
          return false;
        }

        if (
          inventoryFilter === "outOfStock" &&
          firstInventory?.status !== "out_of_stock"
        ) {
          return false;
        }

        return true;
      })
      .sort((a, b) => {
        const aInventory = inventoryByProductId.get(getProductId(a))?.[0];
        const bInventory = inventoryByProductId.get(getProductId(b))?.[0];

        const priority = (product: ProductLike, row?: InventoryStockRow) => {
          if (row?.status === "out_of_stock") return 1;
          if (row?.status === "low_stock") return 2;
          if (!row && !isLikelyNonStockProduct(product)) return 3;
          if (row?.status === "in_stock") return 4;
          return 5;
        };

        return priority(a, aInventory) - priority(b, bInventory);
      });
  }, [
    categoryFilter,
    getDisplayName,
    inventoryByProductId,
    inventoryFilter,
    search,
    sellableProducts,
    sellerUserId,
    statusFilter,
    usersLoading,
  ]);

  const totalPages = Math.max(
    1,
    Math.ceil(productRows.length / PRODUCT_TABLE_PAGE_SIZE),
  );

  const visibleProducts = useMemo(() => {
    const start = (page - 1) * PRODUCT_TABLE_PAGE_SIZE;
    return productRows.slice(start, start + PRODUCT_TABLE_PAGE_SIZE);
  }, [page, productRows]);

  const variantChoices = useMemo(
    () => getVariantChoicesFromProduct(selectedProduct),
    [selectedProduct],
  );

  useEffect(() => {
    if (!defaultSellerUserId) return;

    if (!isSuperAdmin) {
      setSellerUserId(defaultSellerUserId);
    }

    if (isSuperAdmin && !sellerUserId) {
      setSellerUserId(defaultSellerUserId);
    }
  }, [defaultSellerUserId, isSuperAdmin, sellerUserId]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    if (sellerUserId) return;
    if (!sellerOptions.length) return;

    setSellerUserId(sellerOptions[0].sellerId);
  }, [isSuperAdmin, sellerOptions, sellerUserId]);

  useEffect(() => {
    setPage(1);
  }, [search, categoryFilter, statusFilter, inventoryFilter, sellerUserId]);

  useEffect(() => {
    if (!initialProductId || selectedProduct) return;

    const product = sellableProducts.find(
      (item) => getProductId(item) === initialProductId,
    );

    if (product) {
      if (
        !isSuperAdmin &&
        getProductSellerUserId(product) !== defaultSellerUserId
      ) {
        return;
      }

      if (isSuperAdmin) {
        setSellerUserId(getProductSellerUserId(product));
      }

      openManageProduct(product);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellableProducts, initialProductId, selectedProduct, isSuperAdmin]);

  useEffect(() => {
    if (!manageDisclosure.isOpen || !selectedProduct || !selectedProductId) {
      return;
    }

    if (hydratedProductId === selectedProductId) {
      return;
    }

    const stockRow = getFirstStockForProduct(
      selectedProduct,
      selectedProductStockRows,
    );

    const defaultVariant = variantChoices[0];

    if (stockRow) {
      setSelectedInventoryRow(stockRow);
      setForm({
        productId: stockRow.productId,
        sellerUserId: stockRow.sellerUserId,
        warehouseId: stockRow.warehouseId || "",
        variantKey: stockRow.variantKey || "default",
        sku: stockRow.sku || "",
        setOnHandQty: String(stockRow.onHandQty ?? 0),
        quantity: "",
        lowStockThreshold: String(stockRow.lowStockThreshold ?? 3),
        reorderPoint: String(stockRow.reorderPoint ?? 5),
        reason: "Manual stock update",
        note: "",
      });
    } else {
      setSelectedInventoryRow(null);
      setForm({
        ...emptyForm,
        productId: selectedProductId,
        sellerUserId:
          getProductSellerUserId(selectedProduct) ||
          sellerUserId ||
          defaultSellerUserId,
        variantKey: defaultVariant?.variantKey || "default",
        sku: defaultVariant?.sku || "",
        setOnHandQty: String(
          defaultVariant?.stockQty ?? selectedProduct.stockQty ?? 0,
        ),
        lowStockThreshold: "3",
        reorderPoint: "5",
        reason: isLikelyNonStockProduct(selectedProduct)
          ? "Optional inventory created for made-to-order/service product"
          : "Initial stock created from product admin",
        note: "",
      });
    }

    setHydratedProductId(selectedProductId);
  }, [
    defaultSellerUserId,
    hydratedProductId,
    manageDisclosure.isOpen,
    selectedProduct,
    selectedProductId,
    selectedProductStockRows,
    sellerUserId,
    variantChoices,
  ]);

  const refreshAll = async () => {
    await Promise.allSettled([
      refetchProducts(),
      refetchSummary(),
      refetchSellerInventory(),
      selectedProductId ? refetchProductInventory() : Promise.resolve(),
    ]);
  };

  const handleSellerSelect = (sellerId: string) => {
    if (!isSuperAdmin) return;

    setSellerUserId(sellerId);
    setSellerSearch("");
    setPage(1);
  };

  const openManageProduct = (product: ProductLike) => {
    const productId = getProductId(product);
    const productSellerId = getProductSellerUserId(product);

    if (!isSuperAdmin && productSellerId !== defaultSellerUserId) {
      toast({
        title: "Not allowed",
        description: "You can only manage inventory for your own products.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setSelectedProduct(product);
    setSelectedProductId(productId);
    setSelectedInventoryRow(null);
    setHydratedProductId("");

    const defaultVariant = getVariantChoicesFromProduct(product)[0];

    setForm({
      ...emptyForm,
      productId,
      sellerUserId: productSellerId || sellerUserId || defaultSellerUserId,
      variantKey: defaultVariant?.variantKey || "default",
      sku: defaultVariant?.sku || "",
      setOnHandQty: String(defaultVariant?.stockQty ?? product.stockQty ?? 0),
      reason: isLikelyNonStockProduct(product)
        ? "Optional inventory created for made-to-order/service product"
        : "Initial stock created from product admin",
    });

    setSearchParams({ productId });
    manageDisclosure.onOpen();
  };

  const openMovements = (product: ProductLike) => {
    const productId = getProductId(product);
    const productSellerId = getProductSellerUserId(product);

    if (!isSuperAdmin && productSellerId !== defaultSellerUserId) {
      toast({
        title: "Not allowed",
        description:
          "You can only view inventory movements for your own products.",
        status: "warning",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setSelectedProduct(product);
    setSelectedProductId(productId);
    setSearchParams({ productId });
    movementsDisclosure.onOpen();
  };

  const handleVariantChange = (variantKey: string) => {
    const existingStock = selectedProductStockRows.find(
      (row) => row.variantKey === variantKey,
    );

    const variant = variantChoices.find(
      (choice) => choice.variantKey === variantKey,
    );

    if (existingStock) {
      setSelectedInventoryRow(existingStock);
      setForm((prev) => ({
        ...prev,
        variantKey,
        warehouseId: existingStock.warehouseId || "",
        sku: existingStock.sku || variant?.sku || "",
        setOnHandQty: String(existingStock.onHandQty ?? 0),
        quantity: "",
        lowStockThreshold: String(existingStock.lowStockThreshold ?? 3),
        reorderPoint: String(existingStock.reorderPoint ?? 5),
      }));
      return;
    }

    setSelectedInventoryRow(null);
    setForm((prev) => ({
      ...prev,
      variantKey,
      sku: variant?.sku || "",
      setOnHandQty: String(variant?.stockQty ?? selectedProduct?.stockQty ?? 0),
      quantity: "",
      lowStockThreshold: prev.lowStockThreshold || "3",
      reorderPoint: prev.reorderPoint || "5",
    }));
  };

  const handleSaveStock = async () => {
    try {
      if (!form.productId.trim()) {
        toast({
          title: "Product ID required",
          description: "Select a product before updating stock.",
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
        return;
      }

      if (!form.sellerUserId.trim()) {
        toast({
          title: "Seller ID required",
          description: "This product needs a seller user ID for inventory.",
          status: "warning",
          duration: 4000,
          isClosable: true,
        });
        return;
      }

      if (!isSuperAdmin && form.sellerUserId !== defaultSellerUserId) {
        toast({
          title: "Not allowed",
          description: "You can only save inventory for your own products.",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      const basePayload = {
        productId: form.productId.trim(),
        sellerUserId: form.sellerUserId.trim(),
        warehouseId: form.warehouseId.trim(),
        variantKey: form.variantKey.trim() || "default",
        sku: form.sku.trim(),
        lowStockThreshold: Number(form.lowStockThreshold || 0),
        reorderPoint: Number(form.reorderPoint || 0),
        reason:
          form.reason.trim() ||
          (selectedInventoryRow ? "Stock updated" : "Stock created"),
        note: form.note.trim(),
        createdBy: defaultSellerUserId,
        source: "admin" as const,
      };

      if (selectedInventoryRow) {
        await adjustStock({
          ...basePayload,
          setOnHandQty:
            form.setOnHandQty === "" ? undefined : Number(form.setOnHandQty),
          quantity: form.quantity === "" ? undefined : Number(form.quantity),
        }).unwrap();
      } else {
        await createOrUpdateStock({
          ...basePayload,
          setOnHandQty: Number(form.setOnHandQty || 0),
        }).unwrap();
      }

      toast({
        title: selectedInventoryRow ? "Inventory updated" : "Inventory created",
        description: `${getProductTitle(selectedProduct)} is now linked to inventory tracking.`,
        status: "success",
        duration: 4500,
        isClosable: true,
      });

      await refreshAll();
      setHydratedProductId("");
    } catch (err: any) {
      toast({
        title: "Could not save inventory",
        description: getErrorMessage(err, "Please try again."),
        status: "error",
        duration: 7000,
        isClosable: true,
      });
    }
  };

  const handleQuickAdjust = async (
    product: ProductLike,
    row: InventoryStockRow | null,
    quantity: number,
  ) => {
    try {
      const productSellerId = getProductSellerUserId(product);

      if (!isSuperAdmin && productSellerId !== defaultSellerUserId) {
        toast({
          title: "Not allowed",
          description: "You can only adjust inventory for your own products.",
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      if (!row) {
        openManageProduct(product);
        toast({
          title: "Create inventory first",
          description:
            "This product does not have an inventory record yet. Create one, then quick adjustments will be available.",
          status: "info",
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      await adjustStock({
        productId: row.productId,
        sellerUserId: row.sellerUserId,
        warehouseId: row.warehouseId,
        variantKey: row.variantKey || "default",
        quantity,
        reason: `Quick stock adjustment ${quantity > 0 ? "+" : ""}${quantity}`,
        note: "Quick stock adjustment from inventory admin",
        createdBy: defaultSellerUserId,
        source: "admin",
      }).unwrap();

      toast({
        title: "Stock adjusted",
        description: `${getProductTitle(product)} changed by ${
          quantity > 0 ? "+" : ""
        }${quantity}.`,
        status: "success",
        duration: 3500,
        isClosable: true,
      });

      await refreshAll();
    } catch (err: any) {
      toast({
        title: "Stock adjustment failed",
        description: getErrorMessage(err, "Please try again."),
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    }
  };

  const handleExpireReservations = async () => {
    try {
      const expired = await expireReservations().unwrap();

      toast({
        title: "Reservations checked",
        description: `${expired.length} expired reservation(s) released.`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });

      await refreshAll();
    } catch (err: any) {
      toast({
        title: "Could not expire reservations",
        description: getErrorMessage(err, "Please try again."),
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    }
  };

  const renderSellerAccessPanel = () => {
    if (!isSuperAdmin) {
      return (
        <Box
          p={2}
          bg={softBg}
          border={`1px solid ${borderColor}`}
          borderRadius="10px"
        >
          <HStack align="start">
            <Box minW={0}>
              <Text color={mutedText} fontSize="sm" noOfLines={1}>
                {getSellerName(defaultSellerUserId)}
              </Text>
            </Box>
          </HStack>
        </Box>
      );
    }

    return (
      <Box>
        <FormLabel color={textColor}>Seller / Business</FormLabel>

        <Menu placement="bottom-start" closeOnSelect={false}>
          <MenuButton
            as={Button}
            w="100%"
            h="auto"
            py={2.5}
            px={3}
            textAlign="left"
            rightIcon={<ChevronDown size={15} />}
            {...secondaryButtonStyles}
          >
            <HStack spacing={2}>
              <Flex
                boxSize="26px"
                borderRadius="9px"
                align="center"
                justify="center"
                flexShrink={0}
                bg={isDark ? "rgba(49,130,206,0.18)" : "blue.50"}
                color="blue.500"
              >
                <ShieldCheck size={14} />
              </Flex>

              <Box minW={0}>
                <Text color={textColor} fontWeight="900" noOfLines={1}>
                  {selectedSellerOption?.sellerName ||
                    getSellerName(sellerUserId) ||
                    "Select seller"}
                </Text>
                <Text color={mutedText} fontSize="xs" noOfLines={1}>
                  Super admin seller switcher
                </Text>
              </Box>
            </HStack>
          </MenuButton>

          <MenuList
            {...compactMenuListStyles}
            minW={{ base: "300px", md: "420px" }}
            maxW={{ base: "calc(100vw - 32px)", md: "460px" }}
          >
            <Box
              mb={2}
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <Search size={15} color={mutedText} />
                </InputLeftElement>
                <Input
                  value={sellerSearch}
                  onChange={(e) => setSellerSearch(e.target.value)}
                  placeholder="Search seller or business..."
                  bg={softBg}
                  borderColor={borderColor}
                  color={textColor}
                  borderRadius="12px"
                  autoFocus
                />
              </InputGroup>
            </Box>

            <Box maxH="300px" overflowY="auto">
              {filteredSellerOptions.length === 0 ? (
                <Box py={6} textAlign="center">
                  <Text color={textColor} fontWeight="900" fontSize="sm">
                    No sellers found
                  </Text>
                  <Text color={mutedText} fontSize="xs" mt={1}>
                    Only sellers with sellable product listings appear here.
                  </Text>
                </Box>
              ) : (
                <VStack align="stretch" spacing={1}>
                  {filteredSellerOptions.map((seller) => {
                    const isSelected = seller.sellerId === sellerUserId;

                    return (
                      <MenuItem
                        key={seller.sellerId}
                        onClick={() => handleSellerSelect(seller.sellerId)}
                        bg={
                          isSelected
                            ? isDark
                              ? "rgba(49,130,206,0.18)"
                              : "blue.50"
                            : "transparent"
                        }
                        border={`1px solid ${
                          isSelected ? theme.primary : "transparent"
                        }`}
                        borderRadius="12px"
                        px={2}
                        py={2}
                        minH="40px"
                        _hover={{
                          bg: isDark ? "rgba(255,255,255,0.06)" : "blue.50",
                          borderColor: isSelected ? theme.primary : borderColor,
                        }}
                        _focus={{
                          bg: isDark ? "rgba(255,255,255,0.06)" : "blue.50",
                        }}
                        _active={{
                          bg: isDark ? "rgba(255,255,255,0.08)" : "blue.100",
                        }}
                      >
                        <Flex
                          w="100%"
                          justify="space-between"
                          gap={3}
                          align="center"
                        >
                          <HStack spacing={2} minW={0}>
                            <Flex
                              boxSize="26px"
                              borderRadius="9px"
                              align="center"
                              justify="center"
                              bg={isSelected ? "blue.100" : "green.50"}
                              color={isSelected ? "blue.700" : "green.700"}
                              flexShrink={0}
                            >
                              <Store size={14} />
                            </Flex>

                            <Box minW={0}>
                              <Text
                                color={textColor}
                                fontWeight="900"
                                fontSize="sm"
                                noOfLines={1}
                              >
                                {seller.sellerName}
                              </Text>

                              <Text
                                color={mutedText}
                                fontSize="xs"
                                noOfLines={1}
                              >
                                {seller.categories.slice(0, 2).join(" • ")}
                                {seller.categories.length > 2 ? " • ..." : ""}
                              </Text>
                            </Box>
                          </HStack>

                          <Badge
                            colorScheme={isSelected ? "blue" : "green"}
                            borderRadius="full"
                            px={2}
                            py={0.5}
                            fontSize="10px"
                            flexShrink={0}
                          >
                            {seller.productCount}
                          </Badge>
                        </Flex>
                      </MenuItem>
                    );
                  })}
                </VStack>
              )}
            </Box>
          </MenuList>
        </Menu>

        <Text color={mutedText} fontSize="xs" mt={2}>
          Super admins can switch sellers. Regular users only see their own
          inventory.
        </Text>
      </Box>
    );
  };

  const renderSummaryCards = () => (
    <Grid
      templateColumns={{
        base: "1fr",
        sm: "repeat(2, 1fr)",
        xl: "repeat(5, 1fr)",
      }}
      gap={4}
    >
      <StatCard
        icon={<Boxes size={22} />}
        label="Products"
        value={productRows.length}
        help={
          selectedSellerOption
            ? `For ${selectedSellerOption.sellerName}`
            : "Matching products"
        }
        loading={productsLoading || accessLoading}
        cardBg={cardBg}
        borderColor={borderColor}
        textColor={textColor}
        mutedText={mutedText}
      />

      <StatCard
        icon={<PackageCheck size={22} />}
        label="Available"
        value={summary?.totalAvailableQty}
        help="Sellable inventory"
        loading={summaryLoading}
        cardBg={cardBg}
        borderColor={borderColor}
        textColor={textColor}
        mutedText={mutedText}
      />

      <StatCard
        icon={<Warehouse size={22} />}
        label="Reserved"
        value={summary?.totalReservedQty}
        help="Held in checkout/payment"
        loading={summaryLoading}
        cardBg={cardBg}
        borderColor={borderColor}
        textColor={textColor}
        mutedText={mutedText}
      />

      <StatCard
        icon={<AlertTriangle size={22} />}
        label="Low / Out"
        value={`${summary?.lowStockCount || 0} / ${
          summary?.outOfStockCount || 0
        }`}
        help="Needs attention"
        loading={summaryLoading}
        cardBg={cardBg}
        borderColor={borderColor}
        textColor={textColor}
        mutedText={mutedText}
      />

      <StatCard
        icon={<Store size={22} />}
        label={isSuperAdmin ? "Sellers" : "Access"}
        value={isSuperAdmin ? sellerOptions.length : "Own"}
        help={isSuperAdmin ? "With sellable products" : "Current user only"}
        loading={productsLoading || usersLoading}
        cardBg={cardBg}
        borderColor={borderColor}
        textColor={textColor}
        mutedText={mutedText}
      />
    </Grid>
  );

  const renderProductActions = (
    product: ProductLike,
    inventoryRow: InventoryStockRow | null,
    mobile = false,
  ) => (
    <Flex
      w={mobile ? "100%" : "auto"}
      gap={2}
      align="center"
      flexWrap={mobile ? "wrap" : "nowrap"}
    >
      {mobile ? (
        <Button
          flex="1"
          minW="130px"
          leftIcon={<Edit size={16} />}
          size="sm"
          {...secondaryButtonStyles}
          onClick={() => openManageProduct(product)}
        >
          Manage
        </Button>
      ) : (
        <IconButton
          aria-label="Manage inventory"
          icon={<Edit size={16} />}
          size="sm"
          {...secondaryButtonStyles}
          onClick={() => openManageProduct(product)}
        />
      )}

      <IconButton
        aria-label="View movement history"
        icon={<History size={16} />}
        size="sm"
        flexShrink={0}
        {...secondaryButtonStyles}
        onClick={() => openMovements(product)}
      />

      <Menu placement="bottom-end">
        <MenuButton
          as={Button}
          size="sm"
          flex={mobile ? "1" : undefined}
          minW={mobile ? "100px" : undefined}
          rightIcon={<ChevronDown size={14} />}
          {...secondaryButtonStyles}
        >
          More
        </MenuButton>

        <MenuList {...compactMenuListStyles} minW="210px">
          <StyledMenuItem
            icon={<PackagePlus size={14} />}
            label="Restock +1"
            colorScheme="green"
            onClick={() => handleQuickAdjust(product, inventoryRow, 1)}
            textColor={textColor}
            mutedText={mutedText}
            isDark={isDark}
          />

          <StyledMenuItem
            icon={<PackagePlus size={14} />}
            label="Restock +10"
            colorScheme="teal"
            onClick={() => handleQuickAdjust(product, inventoryRow, 10)}
            textColor={textColor}
            mutedText={mutedText}
            isDark={isDark}
          />

          <StyledMenuItem
            icon={<PackageMinus size={14} />}
            label="Adjust -1"
            colorScheme="orange"
            onClick={() => handleQuickAdjust(product, inventoryRow, -1)}
            textColor={textColor}
            mutedText={mutedText}
            isDark={isDark}
          />

          <Divider borderColor={borderColor} my={1} />

          <StyledMenuItem
            icon={<Eye size={14} />}
            label={inventoryRow ? "Edit inventory" : "Create inventory"}
            colorScheme="blue"
            onClick={() => openManageProduct(product)}
            textColor={textColor}
            mutedText={mutedText}
            isDark={isDark}
          />
        </MenuList>
      </Menu>
    </Flex>
  );

  const renderProductTable = (productsToRender: ProductLike[]) => {
    if (productsToRender.length === 0) {
      return (
        <Box py={{ base: 10, md: 16 }} px={4} textAlign="center">
          <Heading size="md" color={textColor}>
            No products found
          </Heading>
          <Text color={mutedText} mt={2}>
            {isSuperAdmin
              ? "Try selecting another seller/business or changing the filters."
              : "No sellable products were found for your account."}
          </Text>
        </Box>
      );
    }

    return (
      <Box w="100%" minW={0}>
        <VStack
          display={{ base: "flex", lg: "none" }}
          align="stretch"
          spacing={3}
          w="100%"
        >
          {productsToRender.map((product) => {
            const productId = getProductId(product);
            const sellerId = getProductSellerUserId(product);
            const sellerName = getSellerName(sellerId);
            const inventoryRowsForProduct =
              inventoryByProductId.get(productId) || [];
            const inventoryRow = inventoryRowsForProduct[0] || null;
            const inventoryBadge = getInventoryBadge({
              product,
              inventoryRow,
            });
            const nonStock = isLikelyNonStockProduct(product);

            return (
              <Box
                key={productId}
                w="100%"
                minW={0}
                p={{ base: 3, sm: 4 }}
                bg={cardBg}
                border={`1px solid ${borderColor}`}
                borderRadius="18px"
                boxShadow={theme.shadowSm}
              >
                <Flex align="flex-start" gap={3} mb={4}>
                  {getProductImage(product) ? (
                    <Image
                      src={getProductImage(product)}
                      alt={getProductTitle(product)}
                      boxSize={{ base: "52px", sm: "60px" }}
                      objectFit="cover"
                      borderRadius="14px"
                      border={`1px solid ${borderColor}`}
                      flexShrink={0}
                    />
                  ) : (
                    <Flex
                      boxSize={{ base: "52px", sm: "60px" }}
                      borderRadius="14px"
                      bg={softBg}
                      align="center"
                      justify="center"
                      border={`1px solid ${borderColor}`}
                      flexShrink={0}
                    >
                      <Boxes size={20} />
                    </Flex>
                  )}

                  <Box minW={0} flex="1">
                    <Text
                      color={textColor}
                      fontWeight="900"
                      fontSize={{ base: "sm", sm: "md" }}
                      noOfLines={2}
                    >
                      {getProductTitle(product)}
                    </Text>

                    <Text
                      color={mutedText}
                      fontSize="xs"
                      mt={1}
                      wordBreak="break-all"
                    >
                      {productId}
                    </Text>

                    {typeof product.price !== "undefined" && (
                      <Text
                        color={textColor}
                        fontSize="sm"
                        fontWeight="800"
                        mt={1}
                      >
                        {formatMoney(product.price)}
                      </Text>
                    )}
                  </Box>

                  <Badge
                    colorScheme={getStatusColor(product.status)}
                    borderRadius="full"
                    px={2.5}
                    py={1}
                    fontSize="10px"
                    flexShrink={0}
                  >
                    {getStatusLabel(product.status || "active")}
                  </Badge>
                </Flex>

                <Grid
                  templateColumns={{
                    base: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                  }}
                  gap={3}
                  mb={4}
                >
                  <Box minW={0}>
                    <Text color={mutedText} fontSize="xs">
                      Seller
                    </Text>
                    <Text
                      color={textColor}
                      fontWeight="800"
                      fontSize="sm"
                      noOfLines={2}
                    >
                      {sellerName}
                    </Text>
                    {sellerId && sellerName !== sellerId && (
                      <Text
                        color={mutedText}
                        fontSize="xs"
                        wordBreak="break-all"
                        mt={0.5}
                      >
                        {sellerId}
                      </Text>
                    )}
                  </Box>

                  <Box minW={0}>
                    <Text color={mutedText} fontSize="xs">
                      Category / Type
                    </Text>
                    <Text
                      color={textColor}
                      fontWeight="800"
                      fontSize="sm"
                      noOfLines={2}
                    >
                      {getProductCategory(product)}
                    </Text>
                    <Flex mt={1} gap={1.5} flexWrap="wrap">
                      {nonStock && (
                        <Badge
                          colorScheme="purple"
                          borderRadius="full"
                          fontSize="10px"
                        >
                          Optional stock
                        </Badge>
                      )}
                      {product.hasVariants && (
                        <Badge
                          colorScheme="cyan"
                          borderRadius="full"
                          fontSize="10px"
                        >
                          Variants
                        </Badge>
                      )}
                    </Flex>
                  </Box>

                  <Box minW={0}>
                    <Text color={mutedText} fontSize="xs">
                      Inventory
                    </Text>
                    <Badge
                      colorScheme={inventoryBadge.color}
                      borderRadius="full"
                      px={2.5}
                      py={1}
                      fontSize="10px"
                      mt={1}
                    >
                      {inventoryBadge.label}
                    </Badge>
                    <Text color={mutedText} fontSize="xs" mt={1}>
                      {inventoryBadge.description}
                    </Text>
                    {inventoryRowsForProduct.length > 1 && (
                      <Text color={mutedText} fontSize="xs">
                        {inventoryRowsForProduct.length} stock rows
                      </Text>
                    )}
                  </Box>

                  <Box minW={0}>
                    <Text color={mutedText} fontSize="xs">
                      Legacy Stock
                    </Text>
                    <Text color={textColor} fontWeight="900" fontSize="lg">
                      {formatNumber(product.stockQty)}
                    </Text>
                  </Box>
                </Grid>

                {renderProductActions(product, inventoryRow, true)}
              </Box>
            );
          })}
        </VStack>

        <Box
          display={{ base: "none", lg: "block" }}
          overflowX="auto"
          w="100%"
          sx={{ WebkitOverflowScrolling: "touch" }}
        >
          <Table variant="simple" size="sm" minW="1050px">
            <Thead>
              <Tr>
                <Th color={mutedText}>Product</Th>
                <Th color={mutedText}>Seller</Th>
                <Th color={mutedText}>Category / Type</Th>
                <Th color={mutedText}>Product Status</Th>
                <Th color={mutedText}>Inventory</Th>
                <Th color={mutedText} isNumeric>
                  Legacy Stock
                </Th>
                <Th color={mutedText}>Actions</Th>
              </Tr>
            </Thead>

            <Tbody>
              {productsToRender.map((product) => {
                const productId = getProductId(product);
                const sellerId = getProductSellerUserId(product);
                const sellerName = getSellerName(sellerId);
                const inventoryRowsForProduct =
                  inventoryByProductId.get(productId) || [];
                const inventoryRow = inventoryRowsForProduct[0] || null;
                const inventoryBadge = getInventoryBadge({
                  product,
                  inventoryRow,
                });
                const nonStock = isLikelyNonStockProduct(product);

                return (
                  <Tr key={productId}>
                    <Td>
                      <HStack spacing={3} minW="300px">
                        {getProductImage(product) ? (
                          <Image
                            src={getProductImage(product)}
                            alt={getProductTitle(product)}
                            boxSize="46px"
                            objectFit="cover"
                            borderRadius="14px"
                            border={`1px solid ${borderColor}`}
                          />
                        ) : (
                          <Flex
                            boxSize="46px"
                            borderRadius="14px"
                            bg={softBg}
                            align="center"
                            justify="center"
                            border={`1px solid ${borderColor}`}
                          >
                            <Boxes size={18} />
                          </Flex>
                        )}

                        <Box minW={0}>
                          <Text
                            color={textColor}
                            fontWeight="800"
                            noOfLines={1}
                          >
                            {getProductTitle(product)}
                          </Text>
                          <Text color={mutedText} fontSize="xs" noOfLines={1}>
                            {productId}
                          </Text>
                          {typeof product.price !== "undefined" && (
                            <Text color={mutedText} fontSize="xs">
                              {formatMoney(product.price)}
                            </Text>
                          )}
                        </Box>
                      </HStack>
                    </Td>

                    <Td>
                      <Text color={textColor} fontWeight="700" noOfLines={1}>
                        {sellerName}
                      </Text>
                      {sellerId && sellerName !== sellerId && (
                        <Text color={mutedText} fontSize="xs" noOfLines={1}>
                          {sellerId}
                        </Text>
                      )}
                    </Td>

                    <Td>
                      <Text color={textColor} fontWeight="700">
                        {getProductCategory(product)}
                      </Text>
                      <HStack mt={1} spacing={2}>
                        {nonStock && (
                          <Badge colorScheme="purple" borderRadius="full">
                            Optional stock
                          </Badge>
                        )}
                        {product.hasVariants && (
                          <Badge colorScheme="cyan" borderRadius="full">
                            Variants
                          </Badge>
                        )}
                      </HStack>
                    </Td>

                    <Td>
                      <Badge
                        colorScheme={getStatusColor(product.status)}
                        borderRadius="full"
                        px={3}
                        py={1}
                      >
                        {getStatusLabel(product.status || "active")}
                      </Badge>
                    </Td>

                    <Td>
                      <Badge
                        colorScheme={inventoryBadge.color}
                        borderRadius="full"
                        px={3}
                        py={1}
                      >
                        {inventoryBadge.label}
                      </Badge>
                      <Text color={mutedText} fontSize="xs" mt={1}>
                        {inventoryBadge.description}
                      </Text>
                      {inventoryRowsForProduct.length > 1 && (
                        <Text color={mutedText} fontSize="xs">
                          {inventoryRowsForProduct.length} stock rows
                        </Text>
                      )}
                    </Td>

                    <Td isNumeric color={textColor} fontWeight="800">
                      {formatNumber(product.stockQty)}
                    </Td>

                    <Td>{renderProductActions(product, inventoryRow)}</Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      </Box>
    );
  };

  const renderStockForm = () => {
    const nonStock = isLikelyNonStockProduct(selectedProduct);
    const hasExistingStock = Boolean(selectedInventoryRow);
    const sellerDisplayName = form.sellerUserId
      ? getSellerName(form.sellerUserId)
      : "";

    return (
      <VStack spacing={4} align="stretch">
        {selectedProduct && (
          <Box
            p={4}
            bg={softBg}
            border={`1px solid ${borderColor}`}
            borderRadius="18px"
          >
            <HStack spacing={3} align="start">
              {getProductImage(selectedProduct) ? (
                <Image
                  src={getProductImage(selectedProduct)}
                  alt={getProductTitle(selectedProduct)}
                  boxSize="56px"
                  objectFit="cover"
                  borderRadius="14px"
                  border={`1px solid ${borderColor}`}
                />
              ) : (
                <Flex
                  boxSize="56px"
                  borderRadius="14px"
                  bg={cardBg}
                  align="center"
                  justify="center"
                  border={`1px solid ${borderColor}`}
                >
                  <Boxes size={22} />
                </Flex>
              )}

              <Box flex="1">
                <Heading size="sm" color={textColor}>
                  {getProductTitle(selectedProduct)}
                </Heading>
                <Text color={mutedText} fontSize="sm">
                  {getProductCategory(selectedProduct)} •{" "}
                  {formatMoney(selectedProduct.price)}
                </Text>
                <Text color={mutedText} fontSize="xs" mt={1}>
                  Product ID: {getProductId(selectedProduct)}
                </Text>
                {nonStock && (
                  <Badge colorScheme="purple" borderRadius="full" mt={2}>
                    Inventory optional for this product type
                  </Badge>
                )}
              </Box>
            </HStack>
          </Box>
        )}

        {productInventoryLoading || productInventoryFetching ? (
          <Flex py={5} justify="center">
            <Spinner color={theme.primary} />
          </Flex>
        ) : null}

        {selectedProductStockRows.length > 0 && (
          <Box>
            <FormLabel color={textColor}>
              Existing stock row / variant
            </FormLabel>
            <Select
              value={form.variantKey}
              onChange={(e) => handleVariantChange(e.target.value)}
              bg={softBg}
              borderColor={borderColor}
              color={textColor}
            >
              {selectedProductStockRows.map((row) => (
                <option key={row._id} value={row.variantKey || "default"}>
                  {row.variantKey || "default"} — {row.availableQty} available /{" "}
                  {row.onHandQty} on hand
                </option>
              ))}

              {variantChoices
                .filter(
                  (choice) =>
                    !selectedProductStockRows.some(
                      (row) => row.variantKey === choice.variantKey,
                    ),
                )
                .map((choice) => (
                  <option key={choice.variantKey} value={choice.variantKey}>
                    Create new: {choice.label}
                  </option>
                ))}
            </Select>
          </Box>
        )}

        {selectedProductStockRows.length === 0 && variantChoices.length > 1 && (
          <Box>
            <FormLabel color={textColor}>Variant</FormLabel>
            <Select
              value={form.variantKey}
              onChange={(e) => handleVariantChange(e.target.value)}
              bg={softBg}
              borderColor={borderColor}
              color={textColor}
            >
              {variantChoices.map((choice) => (
                <option key={choice.variantKey} value={choice.variantKey}>
                  {choice.label}
                </option>
              ))}
            </Select>
          </Box>
        )}

        <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
          <GridItem>
            <FormControl isRequired>
              <FormLabel color={textColor}>Product ID</FormLabel>
              <Input
                value={form.productId}
                isReadOnly
                bg={softBg}
                borderColor={borderColor}
                color={textColor}
              />
            </FormControl>
          </GridItem>

          <GridItem>
            <FormControl isRequired>
              <FormLabel color={textColor}>Seller</FormLabel>
              <Input
                value={sellerDisplayName}
                isReadOnly
                placeholder="Seller display name"
                bg={softBg}
                borderColor={borderColor}
                color={textColor}
                fontWeight="700"
              />

              {form.sellerUserId && (
                <Text color={mutedText} fontSize="xs" mt={1} noOfLines={1}>
                  Seller ID: {form.sellerUserId}
                </Text>
              )}
            </FormControl>
          </GridItem>

          <GridItem>
            <FormControl>
              <FormLabel color={textColor}>Warehouse ID</FormLabel>
              <Input
                value={form.warehouseId}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    warehouseId: e.target.value,
                  }))
                }
                placeholder="blank for default warehouse"
                bg={softBg}
                borderColor={borderColor}
                color={textColor}
              />
            </FormControl>
          </GridItem>

          <GridItem>
            <FormControl>
              <FormLabel color={textColor}>Variant Key</FormLabel>
              <Input
                value={form.variantKey}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    variantKey: e.target.value,
                  }))
                }
                placeholder="default or color=red"
                bg={softBg}
                borderColor={borderColor}
                color={textColor}
              />
            </FormControl>
          </GridItem>

          <GridItem>
            <FormControl>
              <FormLabel color={textColor}>SKU</FormLabel>
              <Input
                value={form.sku}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, sku: e.target.value }))
                }
                placeholder="seller SKU"
                bg={softBg}
                borderColor={borderColor}
                color={textColor}
              />
            </FormControl>
          </GridItem>

          <GridItem>
            <FormControl>
              <FormLabel color={textColor}>Set On Hand Qty</FormLabel>
              <NumberInput
                value={form.setOnHandQty}
                min={0}
                onChange={(value) =>
                  setForm((prev) => ({ ...prev, setOnHandQty: value }))
                }
              >
                <NumberInputField
                  placeholder="absolute stock balance"
                  bg={softBg}
                  borderColor={borderColor}
                  color={textColor}
                />
              </NumberInput>
            </FormControl>
          </GridItem>

          {hasExistingStock && (
            <GridItem>
              <FormControl>
                <FormLabel color={textColor}>Add / Remove Qty</FormLabel>
                <NumberInput
                  value={form.quantity}
                  onChange={(value) =>
                    setForm((prev) => ({ ...prev, quantity: value }))
                  }
                >
                  <NumberInputField
                    placeholder="e.g. 10 or -2"
                    bg={softBg}
                    borderColor={borderColor}
                    color={textColor}
                  />
                </NumberInput>
              </FormControl>
            </GridItem>
          )}

          <GridItem>
            <FormControl>
              <FormLabel color={textColor}>Low Stock Threshold</FormLabel>
              <NumberInput
                value={form.lowStockThreshold}
                min={0}
                onChange={(value) =>
                  setForm((prev) => ({
                    ...prev,
                    lowStockThreshold: value,
                  }))
                }
              >
                <NumberInputField
                  bg={softBg}
                  borderColor={borderColor}
                  color={textColor}
                />
              </NumberInput>
            </FormControl>
          </GridItem>

          <GridItem>
            <FormControl>
              <FormLabel color={textColor}>Reorder Point</FormLabel>
              <NumberInput
                value={form.reorderPoint}
                min={0}
                onChange={(value) =>
                  setForm((prev) => ({ ...prev, reorderPoint: value }))
                }
              >
                <NumberInputField
                  bg={softBg}
                  borderColor={borderColor}
                  color={textColor}
                />
              </NumberInput>
            </FormControl>
          </GridItem>
        </Grid>

        <FormControl>
          <FormLabel color={textColor}>Reason</FormLabel>
          <Input
            value={form.reason}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, reason: e.target.value }))
            }
            placeholder="e.g. Initial stock, restock, damaged adjustment"
            bg={softBg}
            borderColor={borderColor}
            color={textColor}
          />
        </FormControl>

        <FormControl>
          <FormLabel color={textColor}>Internal Note</FormLabel>
          <Textarea
            value={form.note}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, note: e.target.value }))
            }
            placeholder="Optional admin note"
            bg={softBg}
            borderColor={borderColor}
            color={textColor}
          />
        </FormControl>
      </VStack>
    );
  };

  return (
    <Box minH="100vh" bg={pageBg} p={{ base: 3, md: 6 }}>
      <VStack align="stretch" spacing={5}>
        <Flex
          direction={{ base: "column", md: "row" }}
          justify="space-between"
          align={{ base: "stretch", md: "center" }}
          gap={4}
        >
          <Box>
            <Heading color={textColor}>Inventory & Product Stock Admin</Heading>
            <Text color={mutedText} mt={1}>
              {isSuperAdmin
                ? "Super admin mode: choose a seller/business and manage product inventory."
                : "Manage inventory for your own sellable products only."}
            </Text>
          </Box>

          <Flex
            w={{ base: "100%", md: "auto" }}
            direction={{ base: "column", sm: "row" }}
            gap={3}
          >
            <Button
              w={{ base: "100%", sm: "auto" }}
              flex={{ sm: 1, md: "initial" }}
              leftIcon={<RefreshCcw size={16} />}
              {...secondaryButtonStyles}
              onClick={refreshAll}
              isLoading={
                productsLoading ||
                sellerInventoryFetching ||
                productInventoryFetching
              }
            >
              Refresh
            </Button>

            <Button
              w={{ base: "100%", sm: "auto" }}
              flex={{ sm: 1, md: "initial" }}
              leftIcon={<Warehouse size={16} />}
              {...secondaryButtonStyles}
              onClick={handleExpireReservations}
              isLoading={expiringReservations}
            >
              Expire Holds
            </Button>
          </Flex>
        </Flex>

        {renderSummaryCards()}

        <Box
          bg={cardBg}
          border={`1px solid ${borderColor}`}
          borderRadius="24px"
          boxShadow={theme.shadowMd}
          p={{ base: 4, md: 5 }}
        >
          <Grid
            templateColumns={{
              base: "1fr",
              xl: isSuperAdmin ? "360px 1fr" : "320px 1fr",
            }}
            gap={5}
            alignItems="start"
          >
            {renderSellerAccessPanel()}

            <Box>
              <Grid
                templateColumns={{
                  base: "1fr",
                  lg: "1.4fr 170px 170px 190px",
                }}
                gap={4}
              >
                <InputGroup>
                  <InputLeftElement pointerEvents="none">
                    <Search size={18} color={mutedText} />
                  </InputLeftElement>
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search product, seller, category, status..."
                    bg={softBg}
                    borderColor={borderColor}
                    color={textColor}
                  />
                </InputGroup>

                <StyledFilterMenu
                  label="Category"
                  value={categoryFilter}
                  options={categoryOptions}
                  onChange={setCategoryFilter}
                  textColor={textColor}
                  mutedText={mutedText}
                  cardBg={cardBg}
                  softBg={softBg}
                  borderColor={borderColor}
                  borderStrong={borderStrong}
                  isDark={isDark}
                />

                <StyledFilterMenu
                  label="Status"
                  value={statusFilter}
                  options={statusOptions}
                  onChange={setStatusFilter}
                  textColor={textColor}
                  mutedText={mutedText}
                  cardBg={cardBg}
                  softBg={softBg}
                  borderColor={borderColor}
                  borderStrong={borderStrong}
                  isDark={isDark}
                />

                <StyledFilterMenu
                  label="Inventory state"
                  value={inventoryFilter}
                  options={inventoryStateOptions}
                  onChange={setInventoryFilter}
                  textColor={textColor}
                  mutedText={mutedText}
                  cardBg={cardBg}
                  softBg={softBg}
                  borderColor={borderColor}
                  borderStrong={borderStrong}
                  isDark={isDark}
                />
              </Grid>
            </Box>
          </Grid>
        </Box>

        <Box
          bg={cardBg}
          border={`1px solid ${borderColor}`}
          borderRadius="24px"
          boxShadow={theme.shadowMd}
          overflow="hidden"
        >
          <Tabs colorScheme="blue" isLazy>
            <Box overflowX="auto" sx={{ WebkitOverflowScrolling: "touch" }}>
              <TabList px={{ base: 3, md: 4 }} pt={4} minW="max-content">
                <Tab color={textColor} whiteSpace="nowrap">
                  All Products
                </Tab>
                <Tab color={textColor} whiteSpace="nowrap">
                  Inventory Only
                </Tab>
                <Tab color={textColor} whiteSpace="nowrap">
                  Missing Inventory
                </Tab>
                <Tab color={textColor} whiteSpace="nowrap">
                  Optional Inventory
                </Tab>
                <Tab color={textColor} whiteSpace="nowrap">
                  Inventory Alerts
                </Tab>
              </TabList>
            </Box>

            <TabPanels>
              <TabPanel>
                {accessLoading || productsLoading || sellerInventoryLoading ? (
                  <Flex py={20} justify="center">
                    <Spinner size="xl" color={theme.primary} />
                  </Flex>
                ) : (
                  renderProductTable(visibleProducts)
                )}

                {totalPages > 1 && (
                  <Flex
                    direction={{ base: "column", sm: "row" }}
                    justify="space-between"
                    align={{ base: "stretch", sm: "center" }}
                    gap={3}
                    mt={5}
                  >
                    <Text
                      color={mutedText}
                      fontSize={{ base: "sm", md: "md" }}
                      textAlign={{ base: "center", sm: "left" }}
                    >
                      Page {page} of {totalPages} • {productRows.length}{" "}
                      products
                    </Text>

                    <Flex w={{ base: "100%", sm: "auto" }} gap={2}>
                      <Button
                        flex={{ base: 1, sm: "initial" }}
                        size="sm"
                        {...secondaryButtonStyles}
                        isDisabled={page <= 1}
                        onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                      >
                        Previous
                      </Button>
                      <Button
                        flex={{ base: 1, sm: "initial" }}
                        size="sm"
                        {...secondaryButtonStyles}
                        isDisabled={page >= totalPages}
                        onClick={() =>
                          setPage((prev) => Math.min(totalPages, prev + 1))
                        }
                      >
                        Next
                      </Button>
                    </Flex>
                  </Flex>
                )}
              </TabPanel>

              <TabPanel>
                {renderProductTable(
                  productRows.filter((product) => {
                    const productId = getProductId(product);
                    return (
                      (inventoryByProductId.get(productId) || []).length > 0
                    );
                  }),
                )}
              </TabPanel>

              <TabPanel>
                {renderProductTable(
                  productRows.filter((product) => {
                    const productId = getProductId(product);
                    const hasInventory =
                      (inventoryByProductId.get(productId) || []).length > 0;

                    return !hasInventory && !isLikelyNonStockProduct(product);
                  }),
                )}
              </TabPanel>

              <TabPanel>
                {renderProductTable(
                  productRows.filter(isLikelyNonStockProduct),
                )}
              </TabPanel>

              <TabPanel>
                {renderProductTable(
                  productRows.filter((product) => {
                    const inventoryRow = inventoryByProductId.get(
                      getProductId(product),
                    )?.[0];

                    return (
                      inventoryRow?.status === "low_stock" ||
                      inventoryRow?.status === "out_of_stock"
                    );
                  }),
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </VStack>

      <Modal
        isOpen={manageDisclosure.isOpen}
        onClose={manageDisclosure.onClose}
        size={{ base: "full", md: "5xl" }}
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent
          bg={cardBg}
          borderRadius={{ base: 0, md: "24px" }}
          maxH={{ base: "100vh", md: "90vh" }}
        >
          <ModalHeader color={textColor}>
            {selectedInventoryRow ? "Edit Inventory" : "Create Inventory"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>{renderStockForm()}</ModalBody>
          <ModalFooter>
            <Flex
              w="100%"
              direction={{ base: "column-reverse", sm: "row" }}
              justify="flex-end"
              gap={3}
            >
              <Button
                w={{ base: "100%", sm: "auto" }}
                {...secondaryButtonStyles}
                onClick={manageDisclosure.onClose}
              >
                Cancel
              </Button>
              <Button
                w={{ base: "100%", sm: "auto" }}
                {...primaryButtonStyles}
                onClick={handleSaveStock}
                isLoading={creatingStock || adjustingStock}
              >
                {selectedInventoryRow ? "Update Inventory" : "Create Inventory"}
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal
        isOpen={movementsDisclosure.isOpen}
        onClose={movementsDisclosure.onClose}
        size={{ base: "full", md: "5xl" }}
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent
          bg={cardBg}
          borderRadius={{ base: 0, md: "24px" }}
          maxH={{ base: "100vh", md: "90vh" }}
        >
          <ModalHeader color={textColor}>
            Stock Movements
            {selectedProduct && (
              <Text fontSize="sm" color={mutedText} mt={1}>
                {getProductTitle(selectedProduct)}
              </Text>
            )}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <MovementList
              movements={movements}
              loading={productInventoryLoading || productInventoryFetching}
              textColor={textColor}
              mutedText={mutedText}
              cardBg={cardBg}
              softBg={softBg}
              borderColor={borderColor}
              getMovementLabel={getMovementLabel}
              getMovementColor={getMovementColor}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              w={{ base: "100%", sm: "auto" }}
              {...secondaryButtonStyles}
              onClick={movementsDisclosure.onClose}
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

const StyledFilterMenu = ({
  label,
  value,
  options,
  onChange,
  textColor,
  mutedText,
  cardBg,
  softBg,
  borderColor,
  borderStrong,
  isDark,
}: {
  label: string;
  value: string;
  options: Array<{
    label: string;
    value: string;
    description?: string;
  }>;
  onChange: (value: string) => void;
  textColor: string;
  mutedText: string;
  cardBg: string;
  softBg: string;
  borderColor: string;
  borderStrong: string;
  isDark: boolean;
}) => {
  const selectedOption =
    options.find((option) => option.value === value) || options[0];

  return (
    <Menu placement="bottom-start">
      <MenuButton
        as={Button}
        w="100%"
        h="40px"
        px={3}
        textAlign="left"
        rightIcon={<ChevronDown size={14} />}
        bg={softBg}
        color={textColor}
        border={`1px solid ${borderColor}`}
        borderRadius="12px"
        fontWeight="800"
        fontSize="sm"
        _hover={{
          bg: isDark ? "rgba(255,255,255,0.07)" : "white",
          borderColor: borderStrong,
        }}
        _active={{
          bg: isDark ? "rgba(255,255,255,0.09)" : "blue.50",
          borderColor: borderStrong,
        }}
      >
        <Box minW={0}>
          <Text color={mutedText} fontSize="10px" lineHeight="1" mb={0.5}>
            {label}
          </Text>
          <Text color={textColor} fontSize="sm" noOfLines={1}>
            {selectedOption?.label || label}
          </Text>
        </Box>
      </MenuButton>

      <MenuList
        bg={cardBg}
        border={`1px solid ${borderColor}`}
        borderRadius="16px"
        boxShadow="lg"
        p={1.5}
        minW="190px"
        maxH="280px"
        overflowY="auto"
      >
        {options.map((option) => {
          const isSelected = option.value === value;

          return (
            <MenuItem
              key={option.value}
              onClick={() => onChange(option.value)}
              bg={
                isSelected
                  ? isDark
                    ? "rgba(49,130,206,0.18)"
                    : "blue.50"
                  : "transparent"
              }
              color={textColor}
              border={`1px solid ${
                isSelected ? "var(--chakra-colors-blue-400)" : "transparent"
              }`}
              borderRadius="12px"
              px={3}
              py={2}
              minH="38px"
              fontWeight={isSelected ? "900" : "700"}
              _hover={{
                bg: isDark ? "rgba(255,255,255,0.06)" : "blue.50",
                borderColor: isSelected
                  ? "var(--chakra-colors-blue-400)"
                  : borderColor,
              }}
              _focus={{
                bg: isDark ? "rgba(255,255,255,0.06)" : "blue.50",
              }}
              _active={{
                bg: isDark ? "rgba(255,255,255,0.08)" : "blue.100",
              }}
            >
              <Box minW={0}>
                <Text color={textColor} fontSize="sm" noOfLines={1}>
                  {option.label}
                </Text>

                {option.description && (
                  <Text color={mutedText} fontSize="xs" noOfLines={1}>
                    {option.description}
                  </Text>
                )}
              </Box>
            </MenuItem>
          );
        })}
      </MenuList>
    </Menu>
  );
};

const StyledMenuItem = ({
  icon,
  label,
  description,
  colorScheme = "blue",
  onClick,
  textColor,
  mutedText,
  isDark,
}: {
  icon: React.ReactNode;
  label: string;
  description?: string;
  colorScheme?: "blue" | "green" | "teal" | "orange" | "red" | "purple";
  onClick: () => void;
  textColor: string;
  mutedText: string;
  isDark: boolean;
}) => {
  const bgMap = {
    blue: isDark ? "rgba(49,130,206,0.16)" : "blue.50",
    green: isDark ? "rgba(56,161,105,0.16)" : "green.50",
    teal: isDark ? "rgba(49,151,149,0.16)" : "teal.50",
    orange: isDark ? "rgba(221,107,32,0.16)" : "orange.50",
    red: isDark ? "rgba(229,62,62,0.16)" : "red.50",
    purple: isDark ? "rgba(128,90,213,0.16)" : "purple.50",
  };

  const colorMap = {
    blue: "blue.600",
    green: "green.600",
    teal: "teal.600",
    orange: "orange.600",
    red: "red.600",
    purple: "purple.600",
  };

  return (
    <MenuItem
      onClick={onClick}
      bg="transparent"
      borderRadius="12px"
      px={2}
      py={2}
      minH="40px"
      _hover={{
        bg: bgMap[colorScheme],
      }}
      _focus={{
        bg: bgMap[colorScheme],
      }}
      _active={{
        bg: bgMap[colorScheme],
      }}
      sx={{
        ".chakra-menu__icon-wrapper": {
          marginEnd: "0",
        },
      }}
    >
      <HStack spacing={2} w="100%" align="center">
        <Flex
          boxSize="26px"
          borderRadius="9px"
          align="center"
          justify="center"
          bg={bgMap[colorScheme]}
          color={colorMap[colorScheme]}
          flexShrink={0}
        >
          {icon}
        </Flex>

        <Box minW={0}>
          <Text color={textColor} fontWeight="800" fontSize="sm" noOfLines={1}>
            {label}
          </Text>

          {description && (
            <Text color={mutedText} fontSize="xs" noOfLines={1}>
              {description}
            </Text>
          )}
        </Box>
      </HStack>
    </MenuItem>
  );
};

const StatCard = ({
  icon,
  label,
  value,
  help,
  loading,
  cardBg,
  borderColor,
  textColor,
  mutedText,
}: {
  icon: React.ReactNode;
  label: string;
  value: any;
  help: string;
  loading?: boolean;
  cardBg: string;
  borderColor: string;
  textColor: string;
  mutedText: string;
}) => (
  <Box
    bg={cardBg}
    border={`1px solid ${borderColor}`}
    borderRadius="20px"
    p={5}
  >
    <Stat>
      <HStack spacing={3} mb={3}>
        {icon}
        <StatLabel color={mutedText} mb={0}>
          {label}
        </StatLabel>
      </HStack>

      <StatNumber color={textColor}>
        {loading ? <Spinner size="sm" /> : formatNumber(value)}
      </StatNumber>

      <StatHelpText color={mutedText}>{help}</StatHelpText>
    </Stat>
  </Box>
);

const MovementList = ({
  movements,
  loading,
  textColor,
  mutedText,
  cardBg,
  softBg,
  borderColor,
  getMovementLabel,
  getMovementColor,
}: {
  movements: InventoryMovement[];
  loading?: boolean;
  textColor: string;
  mutedText: string;
  cardBg: string;
  softBg: string;
  borderColor: string;
  getMovementLabel: (type?: string) => string;
  getMovementColor: (type?: string) => string;
}) => {
  if (loading) {
    return (
      <Flex py={16} justify="center">
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (!movements.length) {
    return (
      <Box py={12} textAlign="center">
        <Heading size="sm" color={textColor}>
          No movement history yet
        </Heading>
        <Text color={mutedText} mt={2}>
          Movements appear after stock is created, reserved, sold, returned, or
          adjusted.
        </Text>
      </Box>
    );
  }

  return (
    <Box w="100%" minW={0}>
      <VStack
        display={{ base: "flex", lg: "none" }}
        align="stretch"
        spacing={3}
      >
        {movements.map((movement) => (
          <Box
            key={movement._id}
            p={{ base: 3, sm: 4 }}
            bg={cardBg}
            border={`1px solid ${borderColor}`}
            borderRadius="16px"
          >
            <Flex
              justify="space-between"
              align="flex-start"
              gap={3}
              mb={3}
              flexWrap="wrap"
            >
              <Badge
                colorScheme={getMovementColor(movement.type)}
                borderRadius="full"
                px={3}
                py={1}
              >
                {getMovementLabel(movement.type)}
              </Badge>

              <Text color={textColor} fontWeight="900" fontSize="sm">
                Qty: {formatNumber(movement.quantity)}
              </Text>
            </Flex>

            <Grid
              templateColumns={{
                base: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
              }}
              gap={3}
              mb={3}
            >
              <Box p={2.5} bg={softBg} borderRadius="12px" minW={0}>
                <Text color={mutedText} fontSize="xs">
                  On Hand
                </Text>
                <Text color={textColor} fontWeight="800" fontSize="sm">
                  {formatNumber(movement.beforeOnHandQty)} →{" "}
                  {formatNumber(movement.afterOnHandQty)}
                </Text>
              </Box>

              <Box p={2.5} bg={softBg} borderRadius="12px" minW={0}>
                <Text color={mutedText} fontSize="xs">
                  Reserved
                </Text>
                <Text color={textColor} fontWeight="800" fontSize="sm">
                  {formatNumber(movement.beforeReservedQty)} →{" "}
                  {formatNumber(movement.afterReservedQty)}
                </Text>
              </Box>

              <Box p={2.5} bg={softBg} borderRadius="12px" minW={0}>
                <Text color={mutedText} fontSize="xs">
                  Available
                </Text>
                <Text color={textColor} fontWeight="800" fontSize="sm">
                  {formatNumber(movement.beforeAvailableQty)} →{" "}
                  {formatNumber(movement.afterAvailableQty)}
                </Text>
              </Box>

              <Box p={2.5} bg={softBg} borderRadius="12px" minW={0}>
                <Text color={mutedText} fontSize="xs">
                  Source
                </Text>
                <Text
                  color={textColor}
                  fontWeight="800"
                  fontSize="sm"
                  wordBreak="break-word"
                >
                  {movement.source || "-"}
                </Text>
              </Box>
            </Grid>

            <Box mb={3}>
              <Text color={mutedText} fontSize="xs">
                Reason
              </Text>
              <Text color={textColor} fontSize="sm" wordBreak="break-word">
                {movement.reason || movement.note || "-"}
              </Text>
              {movement.orderId && (
                <Text
                  color={mutedText}
                  fontSize="xs"
                  mt={1}
                  wordBreak="break-all"
                >
                  Order: {movement.orderId}
                </Text>
              )}
            </Box>

            <Text color={mutedText} fontSize="xs">
              {movement.createdAt
                ? new Date(movement.createdAt).toLocaleString()
                : "-"}
            </Text>
          </Box>
        ))}
      </VStack>

      <Box
        display={{ base: "none", lg: "block" }}
        overflowX="auto"
        sx={{ WebkitOverflowScrolling: "touch" }}
      >
        <Table size="sm" minW="980px">
          <Thead>
            <Tr>
              <Th color={mutedText}>Type</Th>
              <Th color={mutedText}>Qty</Th>
              <Th color={mutedText}>On Hand</Th>
              <Th color={mutedText}>Reserved</Th>
              <Th color={mutedText}>Available</Th>
              <Th color={mutedText}>Source</Th>
              <Th color={mutedText}>Reason</Th>
              <Th color={mutedText}>Date</Th>
            </Tr>
          </Thead>

          <Tbody>
            {movements.map((movement) => (
              <Tr key={movement._id}>
                <Td>
                  <Badge
                    colorScheme={getMovementColor(movement.type)}
                    borderRadius="full"
                    px={3}
                    py={1}
                  >
                    {getMovementLabel(movement.type)}
                  </Badge>
                </Td>

                <Td color={textColor} fontWeight="800">
                  {movement.quantity}
                </Td>

                <Td color={textColor}>
                  {movement.beforeOnHandQty} → {movement.afterOnHandQty}
                </Td>

                <Td color={textColor}>
                  {movement.beforeReservedQty} → {movement.afterReservedQty}
                </Td>

                <Td color={textColor}>
                  {movement.beforeAvailableQty} → {movement.afterAvailableQty}
                </Td>

                <Td color={textColor}>{movement.source}</Td>

                <Td color={textColor} maxW="260px">
                  <Text noOfLines={2}>
                    {movement.reason || movement.note || "-"}
                  </Text>
                  {movement.orderId && (
                    <Text color={mutedText} fontSize="xs">
                      Order: {movement.orderId}
                    </Text>
                  )}
                </Td>

                <Td color={mutedText}>
                  {movement.createdAt
                    ? new Date(movement.createdAt).toLocaleString()
                    : "-"}
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default InventoryAdminPage;
